/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { BuilderUserContext } from '@gh/builder-shared';
import { createDialogRoute, FormState, FormStateBuilder } from '@gh/common-shared';
import {
  AutocompleteLoadMoreEvent,
  DialogInput,
  getControlValue,
  setControlValue,
} from '@gh/core-ui';
import { Observable } from 'rxjs';

import { ID } from '@gh/common-shared-data';
import { MasterZone } from '../../../offices/resources/master-zone.model';
import { OfficeListItem } from '../../../offices/resources/office-list-item.model';
import { ChecklistsBusinessService } from '../../resources';

export interface ChecklistsExportToOfficeDialogConfig {
  masterZones: MasterZone[];
  corporateView: boolean;
}

export type ChecklistsExportToOfficeDialogResult = {
  office: OfficeListItem;
  masterZoneId?: ID;
  masterZoneName?: string;
};

@Component({
  standalone: false,
  selector: 'gh-checklists-export-to-office-dialog',
  templateUrl: 'checklists-export-to-office-dialog.component.html',
  styleUrls: ['checklists-export-to-office-dialog.component.scss'],
})
export class ChecklistsExportToOfficeDialogComponent implements OnInit, DialogInput<ChecklistsExportToOfficeDialogConfig> {
  input: ChecklistsExportToOfficeDialogConfig;
  form: FormState<any>;

  constructor(private dialogRef: MatDialogRef<ChecklistsExportToOfficeDialogResult>,
              private formStateBuilder: FormStateBuilder,
              private businessService: ChecklistsBusinessService,
              private userContext: BuilderUserContext) {
  }

  get corporateView(): boolean {
    return this.input.corporateView;
  }

  get masterZones(): MasterZone[] {
    return this.input.masterZones || [];
  }

  get canExport(): boolean {
    return !!getControlValue(this.form.group, 'sourceOffice')?.officeId;
  }

  ngOnInit(): void {
    const defaultMasterZone = this.resolveDefaultMasterZone();
    this.form = this.formStateBuilder.buildPlain({
      value: {
        sourceMasterZone: defaultMasterZone,
        sourceOffice: null,
      },
      children: {
        sourceMasterZone: void 0,
        sourceOffice: { required: true },
      },
    });
  }

  loadOffices($event: AutocompleteLoadMoreEvent): void {
    const masterZone = getControlValue(this.form.group, 'sourceMasterZone');
    const masterZoneId = masterZone?.id ?? this.userContext.masterZone?.id;
    const offices$: Observable<OfficeListItem[]> = this.businessService.findOfficesByName($event.query, masterZoneId);
    $event.load(offices$);
  }

  onSourceMasterZoneChange(): void {
    setControlValue(this.form.group, 'sourceOffice', null);
  }

  onExport(): void {
    const office = getControlValue(this.form.group, 'sourceOffice');
    if (!office?.officeId) {
      return;
    }
    const { masterZoneId, masterZoneName } = this.resolveMasterZoneForExport();
    this.dialogRef.close({ office, masterZoneId, masterZoneName });
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  private resolveDefaultMasterZone(): MasterZone | null {
    const userMasterZone = this.userContext.masterZone;
    if (!userMasterZone) {
      return null;
    }
    return this.masterZones.find((mz) => mz.id === userMasterZone.id) ?? null;
  }

  private resolveMasterZoneForExport(): { masterZoneId?: ID; masterZoneName?: string } {
    const selectedMasterZone = getControlValue(this.form.group, 'sourceMasterZone') as MasterZone | null;
    const masterZone = selectedMasterZone ?? this.userContext.masterZone;
    if (!masterZone) {
      return {};
    }
    return {
      masterZoneId: masterZone.id,
      masterZoneName: masterZone.name,
    };
  }
}

export const checklistsExportToOfficeDialogRoute = createDialogRoute({
  component: ChecklistsExportToOfficeDialogComponent,
});
